import React, { ChangeEvent, FC } from 'react';
import UIUtils from 'services/UIUtils';
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import FormControl from '@material-ui/core/FormControl';
import InputLabel from '@material-ui/core/InputLabel';
import { useAppSelector } from 'careand-redux/store';
import UnreadBadge from 'components/Common/UnreadBadge';
import { useAppDispatch } from 'careand-redux/useAppDispatch';
import { GetStaff } from 'careand-redux/selectors';
import { api } from 'careand-redux/api/ApiThunks';
import ClinicUtils from 'services/ClinicUtils';

interface Props {
  id?: string;
  value?: string;
  disabled?: boolean;
  setValue: (value: string) => void;
  style?: React.CSSProperties;
  hideLabel?: boolean;
  unreadCount?: (clinicId: string) => number;
  /**
   * Leave out closed clinics (those forwarding to a replacement) — for
   * contexts that create new bookings or shifts. The selected value stays
   * listed even when closed, so an existing record still shows its clinic.
   * Hidden (pre-launch) clinics stay choosable.
   */
  omitClosed?: boolean;
}

const ClinicSelector = ({
  id,
  value,
  disabled,
  setValue,
  style,
  hideLabel = false,
  unreadCount,
  omitClosed = false,
}: Props) => {
  const allClinics = useAppSelector((state) => state.resource.clinics);
  const clinics = omitClosed ? ClinicUtils.getSelectableClinics(allClinics, value) : allClinics;

  if (clinics.length <= 1) {
    return null;
  }

  return (
    <div className="clinic-select-field" style={style}>
      <FormControl className="default-select-field" style={{ ...UIUtils.SELECT_FIELD_STYLE }}>
        {!hideLabel && <InputLabel>Clinic</InputLabel>}
        <Select
          id={id || 'select-clinic'}
          name="clinic_id"
          disabled={disabled}
          placeholder={value}
          defaultValue={value}
          value={value}
          style={{
            marginTop: hideLabel ? 0 : '15px',
          }}
          onChange={(event: ChangeEvent<{ name?: string; value: unknown }>) =>
            setValue(event.target.value as string)
          }
        >
          {clinics.map((clinic) => (
            <MenuItem id={`menu-item-${clinic.id}`} value={clinic.id} key={clinic.id}>
              <div className="flex-row flex gap-2">
                {clinic.name}
                {unreadCount && (
                  <div className="mr-2">
                    <UnreadBadge unreadCount={unreadCount(clinic.id!)} />
                  </div>
                )}
              </div>
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </div>
  );
};

export const AdminClinicSelector = () => {
  const dispatch = useAppDispatch();
  const role = useAppSelector((state) => state.authentication.role);
  const ownUser = useAppSelector(GetStaff);
  const ownClinicId = ownUser?.clinic_id;
  const isAdmin = role === 'admin';

  if (!isAdmin || !ownUser) {
    return null;
  }

  async function changeClinic(clinic_id: string) {
    let toSubmit = {
      ...ownUser!,
      clinic_id,
    };
    let result = await dispatch(api.Admins.updateAdmin({ id: toSubmit.id!, admin: toSubmit }));
    if (result) {
      window.location.reload();
    }
  }

  return <ClinicSelector value={ownClinicId} setValue={changeClinic} hideLabel={true} />;
};

export default ClinicSelector;
